import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Table,
  Row,
  Col,
  Container,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";

const CapacityBuilding = () => {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  /* ================= STATE ================= */
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [abhiyanList, setAbhiyanList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);

  const [selectedAbhiyan, setSelectedAbhiyan] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [warning, setWarning] = useState("");

  /* ================= SAFE ARRAY ================= */
  const safeArray = (json) =>
    Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];

  /* ================= CHECKBOX DROPDOWN ================= */
  const CheckboxDropdown = ({
    label,
    options,
    idKey,
    nameKey,
    selected,
    setSelected,
    disabled,
    dropdownKey,
  }) => {
    const toggle = () => {
      if (!disabled) {
        setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
      }
    };

    return (
      <Form.Group className="mb-3 position-relative">
        <Form.Label>{label}</Form.Label>

        <div
          className={`form-select ${disabled ? "disabled" : ""}`}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={toggle}
        >
          {selected.length === options.length && options.length > 0
            ? "All Selected"
            : selected.length > 0
            ? `${selected.length} Selected`
            : "All"}
        </div>

        {openDropdown === dropdownKey && !disabled && (
          <div
            className="position-absolute bg-white border rounded shadow-sm w-100"
            style={{
              zIndex: 9999,
              maxHeight: 220,
              overflowY: "auto",
              bottom: "100%",
              marginBottom: "8px",
            }}
          >
            <div className="px-3 py-2 border-bottom fw-bold">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={
                  options.length > 0 && selected.length === options.length
                }
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? options.map((o) => o[idKey]) : []
                  )
                }
              />
            </div>

            {options.map((o) => (
              <div key={o[idKey]} className="px-3 py-2">
                <Form.Check
                  type="checkbox"
                  label={o[nameKey]}
                  checked={selected.includes(o[idKey])}
                  onChange={() => {
                    /* 🔴 TALUKA LIMIT FIX */
                    if (
                      dropdownKey === "taluka" &&
                      !selected.includes(o[idKey]) &&
                      selected.length >= 3
                    ) {
                      setWarning("Please select a maximum of 3 Talukas");
                      return;
                    }

                    setWarning("");

                    setSelected(
                      selected.includes(o[idKey])
                        ? selected.filter((x) => x !== o[idKey])
                        : [...selected, o[idKey]]
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    );
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    fetch(`${apiBaseUrl}/capacitybuilding/districts`)
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTableData(arr);
        setDistrictList(arr);
      });

    fetch(`${apiBaseUrl}/capacitybuilding/abhiyans`)
      .then((r) => r.json())
      .then((json) => setAbhiyanList(safeArray(json)));
  }, [apiBaseUrl]);

  /* ================= ABHIYAN → DISTRICT ================= */
  useEffect(() => {
    if (!selectedAbhiyan.length) return;

    fetch(
      `${apiBaseUrl}/capacitybuilding/abhiyans/${selectedAbhiyan.join(
        ","
      )}/districts`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setDistrictList(arr);
        setTableData(arr);
        setSelectedDistrict([]);
        setSelectedTaluka([]);
        setTalukaList([]);
      });
  }, [selectedAbhiyan, apiBaseUrl]);

  /* ================= DISTRICT → TALUKA ================= */
  useEffect(() => {
    if (!selectedDistrict.length || !selectedAbhiyan.length) return;

    fetch(
      `${apiBaseUrl}/capacitybuilding/abhiyans/${selectedAbhiyan.join(
        ","
      )}/districts/${selectedDistrict.join(",")}/talukas`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTalukaList(arr);
        setTableData(arr);
        setSelectedTaluka([]);
      });
  }, [selectedDistrict, selectedAbhiyan, apiBaseUrl]);

  /* ================= TALUKA → VILLAGE (FIXED) ================= */
  useEffect(() => {
    if (
      !selectedTaluka.length ||
      !selectedDistrict.length ||
      !selectedAbhiyan.length
    )
      return;

    fetch(
      `${apiBaseUrl}/capacitybuilding/abhiyans/${selectedAbhiyan.join(
        ","
      )}/districts/${selectedDistrict.join(
        ","
      )}/talukas/${selectedTaluka.join(",")}/villages`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTableData(arr);
      })
      .catch(() => setTableData([]));
  }, [selectedTaluka, selectedDistrict, selectedAbhiyan, apiBaseUrl]);

  /* ================= SEARCH ================= */
  const filteredView = tableData.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ================= DYNAMIC COLUMN ================= */
  const areaTitle = selectedTaluka.length
    ? "गाव"
    : selectedDistrict.length
    ? "तालुका"
    : "जिल्हा";

  const areaName = (row) =>
    selectedTaluka.length
      ? row.village
      : selectedDistrict.length
      ? row.taluka
      : row.district;

  /* ================= TOTAL ================= */
  const totals = filteredView.reduce(
    (t, r) => {
      t.tbl_workshop_capacity_build += +r.tbl_workshop_capacity_build || 0;
      t.tbl_beneficiary_sankhya += +r.tbl_beneficiary_sankhya || 0;
      t.tbl_total_beneficiary_melave +=
        +r.tbl_total_beneficiary_melave || 0;
      t.tbl_beneficiary_melava += +r.tbl_beneficiary_melava || 0;
      return t;
    },
    {
      tbl_workshop_capacity_build: 0,
      tbl_beneficiary_sankhya: 0,
      tbl_total_beneficiary_melave: 0,
      tbl_beneficiary_melava: 0,
    }
  );

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    const rows = filteredView.map((r, i) => ({
      "अ.क्र": i + 1,
      [areaTitle]: areaName(r),
      "एकूण कार्यशाळा": r.tbl_workshop_capacity_build,
      "कार्यशाळा उपस्थित लाभार्थी": r.tbl_beneficiary_sankhya,
      "एकूण लाभार्थी मेळावे": r.tbl_total_beneficiary_melave,
      "मेळावा उपस्थित लाभार्थी": r.tbl_beneficiary_melava,
    }));

    rows.push({
      "अ.क्र": "",
      [areaTitle]: "Total",
      "एकूण कार्यशाळा": totals.tbl_workshop_capacity_build,
      "कार्यशाळा उपस्थित लाभार्थी": totals.tbl_beneficiary_sankhya,
      "एकूण लाभार्थी मेळावे": totals.tbl_total_beneficiary_melave,
      "मेळावा उपस्थित लाभार्थी": totals.tbl_beneficiary_melava,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Capacity Building");
    XLSX.writeFile(wb, "Capacity_Building_Report.xlsx");
  };

  /* ================= UI ================= */
  return (
    <Container fluid>
      <Row>
        <Col md={2} className="p-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/")}
            className="mb-3 w-100"
          >
            Back
          </Button>

          <div className="p-3 rounded shadow-sm" style={{ backgroundColor: "#EAF0F9" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="fw-bold" style={{ color: "#1F3C88", margin: 0 }}>Filters</h5>
              <div
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: "#EAF2FF",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                }}
              >
                <FaSyncAlt size={16} style={{ color: "#1F3C88" }} />
              </div>
            </div>

            <CheckboxDropdown
              label="अभियान"
              options={abhiyanList}
              idKey="abhiyan_id"
              nameKey="abhiyan_name"
              selected={selectedAbhiyan}
              setSelected={setSelectedAbhiyan}
              dropdownKey="abhiyan"
            />

            <CheckboxDropdown
              label="जिल्हा"
              options={districtList}
              idKey="district_id"
              nameKey="district"
              selected={selectedDistrict}
              setSelected={setSelectedDistrict}
              disabled={!districtList.length}
              dropdownKey="district"
            />

            <CheckboxDropdown
              label="तालुका"
              options={talukaList}
              idKey="taluka_id"
              nameKey="taluka"
              selected={selectedTaluka}
              setSelected={setSelectedTaluka}
              disabled={!talukaList.length}
              dropdownKey="taluka"
            />
          </div>
        </Col>

        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
            <h5 className="fw-bold mb-0" style={{ color: "#1F3C88" }}>
              Capacity Building Report
            </h5>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
              <Form.Group controlId="searchBox" style={{ width: '250px' }}>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: '6px',
                    height: '38px',
                    fontSize: '14px',
                    border: '1px solid #ced4da',
                  }}
                />
              </Form.Group>

              <Button
                style={{
                  backgroundColor: "#4da6ff",
                  borderColor: "#4da6ff",
                }}
                onClick={exportExcel}
              >
                Export Excel
              </Button>
            </div>
          </div>

          {warning && <Alert variant="warning">{warning}</Alert>}

          <div style={{ maxHeight: "500px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "6px" }}>
            <Table striped bordered hover responsive>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 5 }}>
                <tr>
                  <th>अ.क्र</th>
                  <th>{areaTitle}</th>
                  <th>एकूण कार्यशाळा</th>
                  <th>कार्यशाळा उपस्थित लाभार्थी</th>
                  <th>एकूण लाभार्थी मेळावे</th>
                  <th>मेळावा उपस्थित लाभार्थी</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: "#eef3ff", fontWeight: "bold" }}>
                  <td colSpan={2} className="text-center align-middle">Total</td>
                  <td className="text-center align-middle">{totals.tbl_workshop_capacity_build}</td>
                  <td className="text-center align-middle">{totals.tbl_beneficiary_sankhya}</td>
                  <td className="text-center align-middle">{totals.tbl_beneficiary_melava}</td>
                  <td className="text-center align-middle">{totals.tbl_total_beneficiary_melave}</td>
                </tr>

                {filteredView.map((r, i) => (
                  <tr key={i}>
                    <td className="text-center align-middle">{i + 1}</td>
                    <td className="text-center align-middle">{areaName(r)}</td>
                    <td className="text-center align-middle">{r.tbl_workshop_capacity_build}</td>
                    <td className="text-center align-middle">{r.tbl_beneficiary_sankhya}</td>
                    <td className="text-center align-middle">{r.tbl_beneficiary_melava}</td>
                    <td className="text-center align-middle">{r.tbl_total_beneficiary_melave}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default CapacityBuilding;
